import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, filter, map, switchMap, take } from 'rxjs';
import { Project, ProjectStatus } from '../../models/project.model';
import { ProjectService } from '../../services/project.service';
import { TenantUserService } from '../../services/tenant-user.service';
import { AvatarComponent } from '../avatar/avatar.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import {
  ProjektAngelegt,
  ProjektNeuAssistentComponent,
} from '../projekt-neu-assistent/projekt-neu-assistent.component';
import { StatusKind, StatusPillComponent } from '../status-pill/status-pill.component';
import { VerwaltungStore } from '../verwaltung/verwaltung.store';
import { VwKopfComponent } from '../verwaltung/vw-kopf.component';

type Aktion = 'archivieren' | 'wiederherstellen' | 'loeschen';

interface Rueckfrage {
  aktion: Aktion;
  projekt: Project;
}

interface Verantwortlich {
  name: string;
  unbekannt: boolean;
  weitere: number;
  weitereNamen: string;
  alle: string;
}

const PILL: Record<Exclude<ProjectStatus, 'archiviert'>, StatusKind> = {
  aktiv: 'aktiv',
  entwurf: 'inaktiv',
  pausiert: 'pending',
};

@Component({
  selector: 'app-verwaltung-projekte',
  standalone: true,
  imports: [
    AvatarComponent,
    ConfirmDialogComponent,
    MatIconModule,
    MatTooltipModule,
    ProjektNeuAssistentComponent,
    RouterLink,
    StatusPillComponent,
    TranslatePipe,
    VwKopfComponent,
  ],
  templateUrl: './verwaltung-projekte.component.html',
  styleUrl: './verwaltung-projekte.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerwaltungProjekteComponent {
  private readonly projectService = inject(ProjectService);
  private readonly tenantUserService = inject(TenantUserService);
  private readonly translate = inject(TranslateService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  readonly projekte = signal<Project[]>([]);
  readonly zustand = signal<'laedt' | 'bereit' | 'fehler'>('laedt');
  readonly rueckfrage = signal<Rueckfrage | null>(null);
  readonly busy = signal(false);
  readonly angelegtesProjekt = signal<ProjektAngelegt | null>(null);
  private readonly namen = signal<Map<string, string> | null>(null);
  readonly assistentOffen = toSignal(this.route.queryParamMap.pipe(map((p) => p.get('neu') === '1')), {
    initialValue: false,
  });

  readonly zaehlung = computed(() => {
    const archiviert = this.projekte().filter((p) => p.status === 'archiviert').length;
    return { aktiv: this.projekte().length - archiviert, archiviert };
  });

  readonly verantwortliche = computed(() => {
    const namen = this.namen();
    const unbekannt = this.translate.instant('verwaltung.projekte.unbekannt');
    return new Map(
      this.projekte().map((p): [string, Verantwortlich | null] => {
        const ids = p.leadUserIds?.length ? p.leadUserIds : p.leadUserId ? [p.leadUserId] : [];
        if (!namen || ids.length === 0) return [p.id, null];
        const alle = ids.map((id) => namen.get(id) ?? null);
        return [
          p.id,
          {
            name: alle[0] ?? unbekannt,
            unbekannt: alle[0] === null,
            weitere: ids.length - 1,
            weitereNamen: alle
              .slice(1)
              .map((n) => n ?? unbekannt)
              .join(', '),
            alle: alle.map((n) => n ?? unbekannt).join(', '),
          },
        ];
      }),
    );
  });

  constructor() {
    this.laden();
    toObservable(inject(VerwaltungStore).customerId)
      .pipe(
        filter((id): id is string => !!id),
        take(1),
        switchMap((customerId) => this.tenantUserService.getUsersDirectory(customerId)),
        takeUntilDestroyed(),
      )
      .subscribe({
        next: (verzeichnis) => this.namen.set(new Map(verzeichnis.map((p) => [p.id, p.displayName]))),
        error: () => this.namen.set(null),
      });
  }

  laden(): void {
    this.zustand.set('laedt');
    this.projectService.list().subscribe({
      next: (projekte) => {
        this.projekte.set(projekte);
        this.zustand.set('bereit');
      },
      error: () => this.zustand.set('fehler'),
    });
  }

  pillArt(status: ProjectStatus | undefined): StatusKind {
    return status && status !== 'archiviert' ? PILL[status] : 'inaktiv';
  }

  anlegen(): void {
    void this.router.navigate([], { relativeTo: this.route, queryParams: { neu: 1 }, queryParamsHandling: 'merge' });
  }

  assistentSchliessen(): void {
    void this.router.navigate([], { relativeTo: this.route, queryParams: { neu: null }, queryParamsHandling: 'merge' });
  }

  beiAngelegt(ereignis: ProjektAngelegt): void {
    this.angelegtesProjekt.set(ereignis);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { neu: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.laden();
  }

  zeileOeffnen(projekt: Project, event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('a, button, [tabindex]')) return;
    void this.router.navigate(['/projects', projekt.id, 'edit']);
  }

  fragen(projekt: Project, aktion: Aktion): void {
    this.rueckfrage.set({ projekt, aktion });
  }

  abbrechen(): void {
    this.rueckfrage.set(null);
  }

  bestaetigen(): void {
    const frage = this.rueckfrage();
    if (!frage) return;
    this.busy.set(true);
    const { projekt, aktion } = frage;
    const anfrage: Observable<unknown> =
      aktion === 'loeschen'
        ? this.projectService.delete(projekt.id)
        : this.projectService.update(projekt.id, { status: aktion === 'archivieren' ? 'archiviert' : 'aktiv' });
    anfrage.subscribe({
      next: (antwort) => {
        this.busy.set(false);
        this.rueckfrage.set(null);
        if (aktion === 'loeschen') {
          this.entfernen(projekt);
        } else {
          this.projekte.update((liste) =>
            liste.map((p) => (p.id === projekt.id ? { ...p, ...(antwort as Project) } : p)),
          );
          this.fokussieren(`[data-testid="vw-projekt-archivieren"][data-projekt="${projekt.id}"]`);
        }
      },
      error: (fehler: HttpErrorResponse) => this.fehlschlag(aktion, fehler),
    });
  }

  private entfernen(projekt: Project): void {
    const liste = this.projekte();
    const index = liste.findIndex((p) => p.id === projekt.id);
    const rest = liste.filter((p) => p.id !== projekt.id);
    this.projekte.set(rest);
    const ziel = rest[index] ?? rest[index - 1];
    this.fokussieren(
      ziel ? `[data-testid="vw-projekt-name"][data-projekt="${ziel.id}"]` : '[data-testid="vw-projekt-anlegen"]',
    );
  }

  private fehlschlag(aktion: Aktion, fehler: HttpErrorResponse): void {
    this.busy.set(false);
    if (fehler.status === 404) {
      this.rueckfrage.set(null);
      this.melden('verwaltung.projekte.fehler.nichtMehr');
      this.laden();
    } else if (fehler.status === 403) {
      // Bei PROJECT_ARCHIVED meldet der projectArchivedInterceptor den Grund.
      if (fehler.error?.code !== 'PROJECT_ARCHIVED') {
        this.melden('verwaltung.projekte.fehler.berechtigung');
      }
    } else {
      this.melden(`verwaltung.projekte.fehler.${aktion}`);
    }
  }

  private melden(schluessel: string): void {
    this.snackBar.open(this.translate.instant(schluessel), undefined, { duration: 4000 });
  }

  // Der Dialog gibt den Fokus beim Schließen an den Auslöser zurück; danach setzen wir ihn gezielt.
  private fokussieren(selektor: string): void {
    afterNextRender(() => this.host.nativeElement.querySelector<HTMLElement>(selektor)?.focus(), {
      injector: this.injector,
    });
  }
}
